import React from 'react';
import {
  recordStripePayment,
  RecordPaymentError,
  type RecordPaymentErrorKind,
} from '../data/payment/recordStripePayment';

/** After this long confirming, tell the patient it can take a while. Same threshold as checkout. */
export const RECORD_PAYMENT_SLOW_MS = 8_000;

export type RecordPaymentState =
  /** No `payment_intent` in the URL: nothing to confirm. */
  | { status: 'idle' }
  | { status: 'confirming'; slow: boolean }
  | { status: 'recorded'; amountPaid?: number }
  | { status: 'processing' }
  | { status: 'not-paid' }
  | { status: 'failed'; kind: RecordPaymentErrorKind };

/**
 * Asks the server to record the payment named by Stripe's `payment_intent`
 * return parameter (CU-17tm466h63h).
 *
 * ⚠️ ONCE PER MOUNT (and per Try Again), NEVER PER CHART SNAPSHOT. The server's
 * own write fires the chart listener; re-running on that would repeat the
 * CU-17tm466jq2e loop class. So the effect depends on the id and the attempt
 * counter only, not on the chart.
 *
 * ⚠️ INFERRED, NOT MEASURED: the write happens on the server, so an unmount
 * mid-call should lose only the on-screen message. That assumes Cloud Run
 * finishes a request whose client has gone, which matters most during a 20–28 s
 * cold start. Nobody has aborted a call mid-flight and read the chart back
 * (adversarial review F9).
 */
export function useRecordStripePayment(paymentIntentId: string | null) {
  const [attempt, setAttempt] = React.useState(0);
  const [state, setState] = React.useState<RecordPaymentState>(() =>
    paymentIntentId ? { status: 'confirming', slow: false } : { status: 'idle' },
  );

  React.useEffect(() => {
    if (!paymentIntentId) return;
    let live = true;
    setState({ status: 'confirming', slow: false });
    const slowTimer = setTimeout(() => {
      if (live) setState((s) => (s.status === 'confirming' ? { ...s, slow: true } : s));
    }, RECORD_PAYMENT_SLOW_MS);

    recordStripePayment(paymentIntentId)
      .then((res) => {
        if (!live) return;
        setState(
          res.outcome === 'recorded' ? { status: 'recorded', amountPaid: res.amountPaid } : { status: res.outcome },
        );
      })
      .catch((err: unknown) => {
        if (!live) return;
        setState({ status: 'failed', kind: err instanceof RecordPaymentError ? err.kind : 'retryable' });
      })
      .finally(() => clearTimeout(slowTimer));

    return () => {
      live = false;
      clearTimeout(slowTimer);
    };
  }, [paymentIntentId, attempt]);

  const retry = React.useCallback(() => setAttempt((a) => a + 1), []);
  return { state, retry };
}
